"use client"

import Carousel from "@/components/common/Carousel"
import NextImage from "@/components/common/NextImage"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useAspects } from "@/hooks/product_detail/useAspects"
import { useProductName } from "@/hooks/product_detail/useProductName"
import { useSelectedAspect } from "@/hooks/product_detail/useSelectedAspect"
import { useSelectedVariant } from "@/hooks/product_detail/useSelectedVariant"
import { useAppSelector } from "@/hooks/redux"
import { useShownQuickNavCategories } from "@/hooks/store/useShownQuickNavCategories"
import { TypeStoreDetail } from "@/lib/redux/features/store/type"
import { Button, Card, Col, Row, Tabs, theme } from "antd"

const ProductPreview = ({ store }: { store: TypeStoreDetail }) => {
  const productName = useProductName()
  const selectedVariant = useSelectedVariant()
  const bundle = useAppSelector(state => state.product.design_detail.data.bundle)
  const productId = useAppSelector(state => state.product.design_detail.data.id)
  const defaultImage = useAppSelector(state => state.product.design_detail.data.default_image)
  const images = useAppSelector(state => state.product.design_detail.data.images)
  const { aspect: selectedAspect, onSelect } = useSelectedAspect()
  const aspects = useAspects()
  const token = theme.useToken().token
  const shownQuickNavBottom = useShownQuickNavCategories(store)
  const isDesktop = useIsDesktop()

  const validAspects = aspects
    .reduce((arr: any[], aspect) => {
      const passed = (!!selectedVariant?.images[0][aspect.aspect_group_label || aspect.label] || "") && (
        !arr.length || !arr.some(a => ((a.aspect_group_label || a.label) === (aspect.aspect_group_label || aspect.label)))
      )
      if (passed) {
        arr.push(aspect)
      }
      return arr
    }, [])
    .sort((a, b) => {
      if (a.label.includes('back')) {
        return 1
      }
      return -1
    })

    const shownPreviewControllers = validAspects.length > 1

  if (!!bundle) {
    const bundleImages = [
      defaultImage,
      ...images
    ]
    return (
      <Row>
        <Col span={24} style={{ marginBottom: '1rem' }}>
          <Carousel
            className={`product__carousel--${productId}`}
            options={{
              prevNextButtons: false,
              draggable: true,
              pageDots: false,
              contain: true
            }}
          >
            {bundleImages.map((img, idx) => {
              return (
                <div key={idx} style={{ width: '100%' }}>
                  <NextImage
                    src={img}
                    fetchPriority='high'
                    width={700}
                    height={700}
                    style={{ objectFit: 'contain', width: '100%', height: '100%', maxHeight: "calc(100vh - 20.5rem)" }}
                    alt={`${productName} - ${selectedVariant?.attributes.map(attr => attr.label).join(' - ')}`}
                  />
                </div>
              )
            })}
          </Carousel>
        </Col>
        <Col span={24}>
          <Carousel
            options={{
              prevNextButtons: false,
              draggable: true,
              pageDots: false,
              asNavFor: `.product__carousel--${productId}`,
              contain: true
            }}
          >
            {bundleImages.map((img, idx) => {
              return (
                <div key={idx} style={{ width: 'calc(100% / 6)', marginRight: '0.5rem' }}>
                  <NextImage
                    src={img}
                    width={700}
                    height={700}
                    fetchPriority='high'
                    style={{ objectFit: 'contain', width: '100%', maxHeight: "7rem", height: 'auto' }}
                    alt={`${productName} - ${selectedVariant?.attributes.map(attr => attr.label).join(' - ')}`}
                  />
                </div>
              )
            })}
          </Carousel>
        </Col>
      </Row>
    )
  }

  const aspectControllers = shownPreviewControllers && (
    <Row
      justify='center'
      style={{
        marginInline: '-0.5rem', flexDirection: isDesktop ? 'column' : 'row', rowGap: '1rem',
        ...(!isDesktop ? {
          position: 'absolute',
          bottom: '-3.5rem',
          width: '100%'
        } : {})
      }}
    >
      {validAspects.map((aspect) => {
        const selected = selectedAspect?.id === aspect.id;
        return (
          <Col style={{ paddingInline: '0.5rem' }}>
            <Button
              style={{
                height: '7rem',
                width: '7rem',
                minHeight: 'unset',
                maxHeight: 'unset',
                padding: '0.2rem',
                border: `0.3rem solid ${selected ? token.colorPrimary : token.colorBorder}`
              }}
              htmlType="button"
              onClick={() => onSelect(aspect.label)}
            >
              <NextImage
                hiddenSpin
                src={selectedVariant?.images[0][aspect.aspect_group_label || aspect.label] || ""}
                height={100}
                width={100}
                alt={`${productName} - ${selectedVariant?.attributes.map(attr => attr.label).join(' - ')} controller`}
                style={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'contain'
                }}
              />
            </Button>
          </Col>
        )
      })}
    </Row>
  )

  return (
    <Row style={{ marginInline: '-0.5rem', ...(!isDesktop && shownPreviewControllers ? { marginBottom: '3.5rem' } : {}) }} wrap={false}>
      {isDesktop && (
        <Col style={{ paddingInline: '0.5rem' }}>
          {aspectControllers}
        </Col>
      )}
      <Col flex={1} style={{ paddingInline: '0.5rem' }}>
        <Card
          variant='borderless'
          size='small'
          styles={{
            body: {
              padding: 0
            }
          }}
          style={{
            // backgroundColor: 'transparent',
            boxShadow: '0 4px 8px 0 rgba(21, 21, 35, 0.2)'
          }}
        >
          <Tabs
            className="product__preview--tabs"
            renderTabBar={() => <></>}
            onChange={onSelect}
            activeKey={selectedAspect?.label}
            items={validAspects.map(aspect => ({
              label: aspect.name,
              key: aspect.label,
              tabKey: aspect.label,
              children: (
                <NextImage
                  fetchPriority="high"
                  src={selectedVariant?.images[0][aspect.aspect_group_label || aspect.label] || ""}
                  width={720}
                  height={900}
                  style={{ objectFit: 'contain', width: '100%', height: '100%', maxHeight: shownQuickNavBottom ? "calc(100vh - 11.25rem)" : "calc(100vh - 7.76rem)" }}
                  alt={`${productName} - ${selectedVariant?.attributes.map(attr => attr.label).join(' - ')}`}
                />
              )
            }))}
          />
          {!isDesktop && aspectControllers}
        </Card>
      </Col>
    </Row>
  )
}

export default ProductPreview